import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePostHog } from '@posthog/react';
import { ONBOARDING_EVENT, capture } from '../../analytics/posthog';
import config from '../../config';
import { getFirstNameFromStorage } from '../../utils/helper';
import { Icon } from './icons';
import styles from './ExtendedWelcomeFlow.module.css';
import ContentParts from '../shared/ContentParts';
import { fillTemplate, getCachedContentCatalog, useContentCatalog } from '../../utils/contentCatalog';
import logoImage from '../../assets/logo.png';
import topoQuestion from '../../assets/extended-welcome-flow/topo-question.png';
import topoInsight from '../../assets/extended-welcome-flow/topo-insight.png';
import topoFuture from '../../assets/extended-welcome-flow/topo-future.png';
import topoFutureStep from '../../assets/extended-welcome-flow/topo-future-step.png';
import topoCommit from '../../assets/extended-welcome-flow/topo-commit.png';
import cardClarity from '../../assets/extended-welcome-flow/card-clarity.jpg';
import cardMomentum from '../../assets/extended-welcome-flow/card-momentum.jpg';
import cardConfidence from '../../assets/extended-welcome-flow/card-confidence.jpg';

const STEP_COUNT = 5;
const HOLD_MS = 2000;
const PILLAR_CAROUSEL_QUERY = '(max-width: 1100px)';

const PILLAR_ART = {
  clarity: { image: cardClarity, position: 'center 68%' },
  momentum: { image: cardMomentum, position: 'center 58%' },
  confidence: { image: cardConfidence, position: 'center 48%' },
};

function welcomeFlowFromCache() {
  return getCachedContentCatalog()?.welcome_flow || null;
}

function knownBlockerIds() {
  const blockers = welcomeFlowFromCache()?.blockers;
  if (!Array.isArray(blockers) || !blockers.length) return null;
  return new Set(blockers.map((item) => item.id));
}

function blockerLabel(id) {
  return welcomeFlowFromCache()?.blockers?.find((item) => item.id === id)?.label || '';
}

// Every panel path shares the same command structure so the `d` transition can morph between steps.
const STEPS = [
  {
    id: 'blocker',
    theme: 'question',
    stepper: 'light',
    background: topoQuestion,
    panel: 'M0 0H690C770 60 800 140 790 240C780 330 820 380 830 470C840 560 820 640 750 730C680 800 620 860 600 900L600 900H0Z',
  },
  {
    id: 'insight',
    theme: 'insight',
    stepper: 'dark',
    background: topoInsight,
    panel: 'M0 0H720C790 70 800 170 820 260C845 360 900 420 860 500C820 580 700 620 620 700C560 760 500 840 470 900L470 900H0Z',
  },
  {
    id: 'future',
    theme: 'future',
    stepper: 'light',
    background: topoFutureStep,
    panel: 'M0 0H630C720 40 780 120 790 210C800 280 870 310 860 400C850 480 780 530 720 600C650 680 560 800 460 900L460 900H0Z',
  },
  {
    id: 'experience',
    theme: 'experience',
    stepper: 'cyan',
    background: topoFuture,
    panel: 'M0 0H660C720 40 760 90 830 140C900 190 960 170 1010 220C1070 280 1100 380 1180 420C1260 460 1350 440 1440 430L1440 900H0Z',
  },
  {
    id: 'commit',
    theme: 'commit',
    stepper: 'dark',
    background: topoCommit,
    panel: 'M0 0H680C760 50 790 140 780 230C770 300 820 350 840 420C860 500 800 560 700 620C600 690 520 790 460 900L460 900H0Z',
  },
];

function readBlocker() {
  try {
    const raw = localStorage.getItem(config.STORAGE_KEYS.EXTENDED_WELCOME_BLOCKER);
    if (!raw) return { id: '', detail: '', label: '' };
    const parsed = JSON.parse(raw);
    const candidate = typeof parsed?.id === 'string' ? parsed.id : '';
    const ids = knownBlockerIds();
    const id = !candidate || (ids && !ids.has(candidate)) ? '' : candidate;
    const detail = typeof parsed?.detail === 'string' ? parsed.detail : '';
    const label = typeof parsed?.label === 'string' ? parsed.label : '';
    return { id, detail, label };
  } catch {
    return { id: '', detail: '', label: '' };
  }
}

export function readExtendedWelcomeBlocker() {
  const saved = readBlocker();
  return saved.label || blockerLabel(saved.id);
}

function persistBlocker(id, detail) {
  try {
    localStorage.setItem(
      config.STORAGE_KEYS.EXTENDED_WELCOME_BLOCKER,
      JSON.stringify({
        id,
        detail: id === 'other' ? detail.trim() : '',
        label: blockerLabel(id),
      })
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function letterDates(now) {
  const future = new Date(now.getTime());
  future.setFullYear(future.getFullYear() + 1);
  const badge = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(future);
  const anniversary = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(now);
  return { badge, anniversary, fromYear: future.getFullYear() };
}

function Stepper({ step }) {
  return (
    <div className={styles.stepper}>
      <div className={styles.dots} aria-hidden="true">
        {STEPS.map((item, index) => {
          const state = index < step ? styles.dotDone : index === step ? styles.dotActive : '';
          return (
            <span
              key={item.id}
              className={state ? `${styles.dot} ${state}` : styles.dot}
            />
          );
        })}
      </div>
      <p key={step} className={styles.stepLabel}>Step {step + 1} of {STEP_COUNT}</p>
    </div>
  );
}

function Brand() {
  return (
    <div className={styles.brand}>
      <img src={logoImage} alt="" className={styles.logo} />
      <span className={styles.wordmark}>Richuel</span>
    </div>
  );
}

function BlockerStep({ blockers, copy, blocker, detail, onSelect, onDetail }) {
  const title = [copy?.title_before, copy?.title_focus, copy?.title_after].filter(Boolean).join(' ');
  return (
    <div className={styles.stage} key="blocker">
      <p className={styles.eyebrow}>{copy?.eyebrow}</p>
      <h1 className={styles.title} tabIndex={-1}>
        {copy?.title_before} <br className={styles.mobileBreak} />
        {copy?.title_focus} <br className={styles.deskBreak} />
        {copy?.title_after}
      </h1>
      <p className={styles.lede}>{copy?.lede}</p>
      <div className={styles.options} role="radiogroup" aria-label={title}>
        {(blockers || []).map((item) => {
          const selected = blocker === item.id;
          return (
            <label
              key={item.id}
              className={selected ? `${styles.option} ${styles.optionSelected}` : styles.option}
            >
              <input
                className={styles.radioInput}
                type="radio"
                name="extended-welcome-blocker"
                value={item.id}
                checked={selected}
                onChange={() => onSelect(item.id)}
              />
              <span className={styles.radio} aria-hidden="true" />
              <span className={styles.optionIcon} aria-hidden="true">
                <Icon name={item.icon} />
              </span>
              <span className={styles.optionMain}>
                <span className={styles.optionText}>{item.label}</span>
                {item.id === 'other' && (
                  <input
                    className={styles.detailInput}
                    type="text"
                    value={detail}
                    placeholder={copy?.other_placeholder}
                    maxLength={280}
                    aria-label={copy?.other_label}
                    onFocus={() => onSelect('other')}
                    onChange={(event) => onDetail(event.target.value)}
                  />
                )}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function InsightStep({ insight, copy }) {
  if (!insight) return null;

  return (
    <div className={styles.stage} key={insight.title || 'insight'}>
      <p className={styles.eyebrow}>{copy?.eyebrow}</p>
      <h1 className={styles.title} tabIndex={-1}>{insight.title}</h1>
      <p className={styles.lede}>{insight.lede}</p>
      <article className={styles.fact}>
        <div className={styles.factHead}>
          <span className={styles.factIcon} aria-hidden="true">
            <Icon name="chart" />
          </span>
          <h2 className={styles.factTitle}>{copy?.fact_title}</h2>
        </div>
        <p className={styles.factBody}><ContentParts parts={insight.body} /></p>
        {insight.source ? (
          <p className={styles.source}>
            <Icon name="doc" className={styles.sourceIcon} />
            <span>{copy?.source_label}: {insight.source}</span>
          </p>
        ) : null}
      </article>
    </div>
  );
}

function FutureStep({ letter, copy, firstName, dates, onContinue, onChangeAnswer }) {
  const greetingName = firstName || copy?.fallback_greeting || 'there';
  const futureName = firstName || copy?.fallback_future_name || 'you';
  const values = {
    greeting: greetingName,
    future_name: futureName,
    from_year: dates.fromYear,
    anniversary: dates.anniversary,
    decision: letter?.decision || '',
    outcome: letter?.outcome || '',
  };

  return (
    <div className={styles.stage} key={letter?.decision || 'future'}>
      <p className={styles.datePill}>
        <Icon name="calendar" className={styles.dateIcon} />
        <span>{dates.badge}</span>
      </p>
      <h1 className={styles.title} tabIndex={-1}>
        {fillTemplate(copy?.title_line_1, values)}
        <br />
        {fillTemplate(copy?.title_line_2, values)}
        <Icon name="sparkles" className={styles.sparkles} />
      </h1>
      <p className={styles.letter}>{fillTemplate(copy?.letter_open, values)}</p>
      <p className={styles.letter}>{fillTemplate(copy?.letter_close, values)}</p>
      <button type="button" className={styles.letsGo} onClick={onContinue}>
        {copy?.continue_label}
        <Icon name="arrowRight" className={styles.btnIcon} />
      </button>
      <button type="button" className={styles.changeAnswer} onClick={onChangeAnswer}>
        {copy?.change_answer_label}
      </button>
    </div>
  );
}

function moduleHoverSupported() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function ModuleDialog({ module, closeLabel, dismissOnBackdrop, onClose, onDialogEnter, onDialogLeave }) {
  const titleId = 'experience-module-title';

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      className={dismissOnBackdrop ? `${styles.moduleOverlay} ${styles.moduleOverlayTouch}` : styles.moduleOverlay}
      onClick={dismissOnBackdrop ? (event) => {
        if (event.target === event.currentTarget) onClose();
      } : undefined}
    >
      <div
        id="experience-module-dialog"
        className={styles.moduleDialog}
        role="dialog"
        aria-modal={dismissOnBackdrop ? 'true' : undefined}
        aria-labelledby={titleId}
        onPointerEnter={onDialogEnter}
        onPointerLeave={(event) => {
          const next = event.relatedTarget;
          if (next instanceof Element && next.closest('[data-module-trigger]')) return;
          onDialogLeave?.(event);
        }}
      >
        <button
          type="button"
          className={styles.moduleClose}
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          aria-label={closeLabel || 'Close'}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <h2 id={titleId} className={styles.moduleDialogTitle}>{module.title}</h2>
        <p className={styles.moduleDialogBody}>{module.detail}</p>
      </div>
    </div>
  );
}

function ExperienceStep({ pillars = [], copy, activeModule, onHoverModule, onTouchModule, onLeaveModule, onSlideChange }) {
  const pointerKind = useRef('mouse');
  const scrollerRef = useRef(null);
  const indexRef = useRef(0);
  const programmaticRef = useRef(false);
  const [pillarIndex, setPillarIndex] = useState(0);
  const [isCarousel, setIsCarousel] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(PILLAR_CAROUSEL_QUERY).matches,
  );

  const syncPillarIndex = useCallback((next) => {
    const clamped = Math.max(0, Math.min(Math.max(pillars.length - 1, 0), next));
    indexRef.current = clamped;
    setPillarIndex((current) => (current === clamped ? current : clamped));
  }, [pillars.length]);

  const readClosestPillar = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return 0;
    const origin = el.getBoundingClientRect().left;
    let closest = 0;
    let min = Infinity;
    [...el.children].forEach((slide, index) => {
      const delta = Math.abs(slide.getBoundingClientRect().left - origin);
      if (delta < min) {
        min = delta;
        closest = index;
      }
    });
    return closest;
  }, []);

  useEffect(() => {
    const media = window.matchMedia(PILLAR_CAROUSEL_QUERY);
    const el = scrollerRef.current;
    const applyMode = () => setIsCarousel(media.matches);
    const syncFromScroll = () => {
      if (!media.matches) return;
      if (programmaticRef.current) {
        syncPillarIndex(indexRef.current);
        return;
      }
      syncPillarIndex(readClosestPillar());
    };
    const finishScroll = () => {
      programmaticRef.current = false;
      if (media.matches) syncPillarIndex(readClosestPillar());
    };
    applyMode();
    media.addEventListener('change', applyMode);
    el?.addEventListener('scroll', syncFromScroll, { passive: true });
    el?.addEventListener('scrollend', finishScroll);
    window.addEventListener('resize', syncFromScroll);
    return () => {
      media.removeEventListener('change', applyMode);
      el?.removeEventListener('scroll', syncFromScroll);
      el?.removeEventListener('scrollend', finishScroll);
      window.removeEventListener('resize', syncFromScroll);
    };
  }, [readClosestPillar, syncPillarIndex]);

  const slideChanged = useRef(false);
  useEffect(() => {
    if (!slideChanged.current) {
      slideChanged.current = true;
      return;
    }
    onSlideChange?.();
  }, [pillarIndex, onSlideChange]);

  const goToPillar = useCallback((next) => {
    const el = scrollerRef.current;
    if (!el || !window.matchMedia(PILLAR_CAROUSEL_QUERY).matches) return;
    const clamped = Math.max(0, Math.min(Math.max(pillars.length - 1, 0), next));
    const slide = el.children[clamped];
    if (!slide) return;
    programmaticRef.current = true;
    syncPillarIndex(clamped);
    const left = slide.getBoundingClientRect().left - el.getBoundingClientRect().left + el.scrollLeft;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
    window.setTimeout(() => {
      if (!programmaticRef.current) return;
      programmaticRef.current = false;
      syncPillarIndex(indexRef.current);
    }, 520);
  }, [pillars.length, syncPillarIndex]);

  return (
    <div className={styles.stageWide} key="experience">
      <p className={styles.eyebrow}>{copy?.eyebrow}</p>
      <h1 className={styles.title} tabIndex={-1}>{copy?.title}</h1>
      <p className={styles.lede}>
        {copy?.lede}
      </p>
      <div className={styles.pillarCarousel}>
      <div
        className={styles.pillarRow}
        ref={scrollerRef}
        tabIndex={isCarousel ? 0 : undefined}
        role={isCarousel ? 'region' : undefined}
        aria-roledescription={isCarousel ? 'carousel' : undefined}
        aria-label={isCarousel ? copy?.carousel_label : undefined}
        onKeyDown={isCarousel ? (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goToPillar(indexRef.current + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goToPillar(indexRef.current - 1);
          }
        } : undefined}
      >
        {pillars.map((pillar, index) => {
          const art = PILLAR_ART[pillar.art] || {};
          return (
          <article
            key={pillar.n}
            className={styles.pillar}
            aria-roledescription={isCarousel ? 'slide' : undefined}
            aria-label={isCarousel ? `${index + 1} of ${pillars.length}` : undefined}
          >
            <div
              className={styles.pillarArt}
              style={{
                backgroundImage: art.image ? `url(${art.image})` : undefined,
                backgroundPosition: art.position,
              }}
            >
              <span className={styles.badge}>{pillar.n}</span>
            </div>
            <div className={styles.pillarBody}>
              <h2 className={styles.pillarTitle}>{pillar.title}</h2>
              <p className={styles.pillarText}>{pillar.body}</p>
              <p className={styles.kicker}>{pillar.kicker}</p>
              <div className={styles.modules}>
                {pillar.modules.map((mod) => {
                  const isOpen = activeModule?.title === mod.title;
                  return (
                    <button
                      key={mod.title}
                      type="button"
                      className={styles.module}
                      data-module-trigger=""
                      aria-haspopup="dialog"
                      aria-expanded={isOpen}
                      aria-controls={isOpen ? 'experience-module-dialog' : undefined}
                      onPointerDown={(event) => {
                        pointerKind.current = event.pointerType;
                      }}
                      onPointerEnter={(event) => {
                        if (event.pointerType === 'touch' || !moduleHoverSupported()) return;
                        onHoverModule(mod);
                      }}
                      onPointerLeave={(event) => {
                        if (event.pointerType === 'touch' || !moduleHoverSupported()) return;
                        if (event.currentTarget.matches(':focus-visible')) return;
                        const next = event.relatedTarget;
                        if (next instanceof Element && next.closest('#experience-module-dialog')) return;
                        onLeaveModule(mod);
                      }}
                      onFocus={(event) => {
                        if (!event.currentTarget.matches(':focus-visible')) return;
                        onHoverModule(mod);
                      }}
                      onBlur={(event) => {
                        if (event.currentTarget.matches(':hover') && moduleHoverSupported()) return;
                        onLeaveModule(mod);
                      }}
                      onClick={(event) => {
                        if (event.detail === 0) return;
                        if (pointerKind.current === 'touch' || !moduleHoverSupported()) onTouchModule(mod);
                      }}
                    >
                      <Icon name={mod.icon} className={`${styles.moduleIcon} ${styles[mod.tone] || ''}`} />
                      <span className={styles.moduleLabel}>{mod.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </article>
          );
        })}
      </div>
      <div className={styles.pillarNav}>
        <button
          type="button"
          className={styles.pillarSwap}
          aria-label={copy?.previous_label}
          disabled={pillarIndex === 0}
          onClick={() => goToPillar(pillarIndex - 1)}
        >
          <Icon name="arrowLeft" className={styles.pillarSwapIcon} />
        </button>
        <div className={styles.pillarDots}>
          {pillars.map((pillar, index) => (
            <button
              key={pillar.n}
              type="button"
              className={index === pillarIndex ? `${styles.pillarDot} ${styles.pillarDotActive}` : styles.pillarDot}
              aria-label={pillar.title}
              aria-current={index === pillarIndex ? 'true' : undefined}
              onClick={() => goToPillar(index)}
            />
          ))}
        </div>
        <button
          type="button"
          className={styles.pillarSwap}
          aria-label={copy?.next_label}
          disabled={pillarIndex === pillars.length - 1}
          onClick={() => goToPillar(pillarIndex + 1)}
        >
          <Icon name="arrowRight" className={styles.pillarSwapIcon} />
        </button>
        <p className={styles.pillarLive} aria-live="polite">
          {pillars[pillarIndex]?.title}, {pillarIndex + 1} of {pillars.length}
        </p>
      </div>
      </div>
    </div>
  );
}

function CommitStep({ copy, onHoldStart, onHoldEnd, fillRef, buttonRef, commitError }) {
  return (
    <div className={styles.stage} key="commit">
      <p className={styles.eyebrow}>{copy?.eyebrow}</p>
      <h1 className={styles.title} tabIndex={-1}>
        {copy?.title_before} <br className={styles.mobileBreak} />
        {copy?.title_focus} <br className={styles.deskBreak} />
        {copy?.title_after}
      </h1>
      <p className={styles.lede}>{copy?.lede}</p>
      <p className={styles.holdPrompt}>{copy?.hold_prompt}</p>
      <div className={styles.holdWrap}>
        <button
          ref={buttonRef}
          type="button"
          className={styles.hold}
          aria-label={copy?.hold_aria}
          onPointerDown={onHoldStart}
          onPointerUp={onHoldEnd}
          onPointerCancel={onHoldEnd}
          onKeyDown={onHoldStart}
          onKeyUp={onHoldEnd}
          onContextMenu={(event) => event.preventDefault()}
        >
          <span ref={fillRef} className={styles.holdFill} />
          <span className={styles.holdIcon} aria-hidden="true">
            <Icon name="cursor" />
          </span>
          <span className={styles.holdLabel}>{copy?.hold_label}</span>
        </button>
        <p className={styles.holdHint}>
          <Icon name="grip" className={styles.grip} />
          {copy?.hold_hint}
        </p>
        {commitError ? (
          <p className={styles.saveError} role="alert">{commitError}</p>
        ) : null}
      </div>
    </div>
  );
}

const ExtendedWelcomeFlow = ({
  entry = 'signup',
  firstName: firstNameProp = '',
  onCommitted,
  onExit,
}) => {
  const posthog = usePostHog();
  const { catalog, loading: catalogLoading, error: catalogError, retry: retryCatalog } = useContentCatalog();
  const flow = catalog?.welcome_flow;
  const flowCopy = flow?.copy || {};
  const saved = useMemo(() => readBlocker(), []);
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [blocker, setBlocker] = useState(saved.id);
  const [detail, setDetail] = useState(saved.detail);
  const [commitError, setCommitError] = useState('');
  const [modulePreview, setModulePreview] = useState(null);
  const holdRef = useRef({ raf: 0, timer: 0, active: false, done: false });
  const fillRef = useRef(null);
  const holdButtonRef = useRef(null);
  const dates = useMemo(() => letterDates(new Date()), []);
  const firstName = firstNameProp || getFirstNameFromStorage();
  const current = STEPS[step];

  useEffect(() => {
    capture(posthog, ONBOARDING_EVENT, {
      step: 'extended_welcome',
      welcome_step: step + 1,
      entry,
    });
  }, [entry, posthog, step]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      htmlHeight: html.style.height,
      bodyHeight: body.style.height,
      bodyOverscroll: body.style.overscrollBehavior,
    };
    html.style.overflow = 'hidden';
    html.style.height = '100%';
    body.style.overflow = 'hidden';
    body.style.height = '100%';
    body.style.overscrollBehavior = 'none';
    return () => {
      html.style.overflow = previous.htmlOverflow;
      html.style.height = previous.htmlHeight;
      body.style.overflow = previous.bodyOverflow;
      body.style.height = previous.bodyHeight;
      body.style.overscrollBehavior = previous.bodyOverscroll;
    };
  }, []);

  const moduleHideTimer = useRef(0);

  useEffect(() => () => {
    cancelAnimationFrame(holdRef.current.raf);
    window.clearTimeout(holdRef.current.timer);
    window.clearTimeout(moduleHideTimer.current);
  }, []);

  const cancelModuleHide = useCallback(() => {
    window.clearTimeout(moduleHideTimer.current);
  }, []);

  const closeModule = useCallback(() => {
    window.clearTimeout(moduleHideTimer.current);
    setModulePreview(null);
  }, []);

  const showModule = useCallback((mod) => {
    window.clearTimeout(moduleHideTimer.current);
    setModulePreview({ module: mod, touch: false });
  }, []);

  const showModuleFromTouch = useCallback((mod) => {
    window.clearTimeout(moduleHideTimer.current);
    setModulePreview({ module: mod, touch: true });
  }, []);

  const scheduleModuleHide = useCallback((mod) => {
    window.clearTimeout(moduleHideTimer.current);
    moduleHideTimer.current = window.setTimeout(() => {
      setModulePreview((current) => {
        if (!current || current.touch) return current;
        if (mod && current.module.title !== mod.title) return current;
        return null;
      });
    }, 280);
  }, []);

  const leaveModule = useCallback((mod) => {
    scheduleModuleHide(mod);
  }, [scheduleModuleHide]);

  const leaveDialog = useCallback(() => {
    scheduleModuleHide(null);
  }, [scheduleModuleHide]);

  const goTo = useCallback((next) => {
    const target = Math.max(0, Math.min(STEP_COUNT - 1, next));
    setModulePreview(null);
    setDirection(target >= step ? 1 : -1);
    setStep(target);
  }, [step]);

  const goBack = useCallback(() => {
    if (step === 0) {
      onExit?.();
      return;
    }
    goTo(step - 1);
  }, [goTo, onExit, step]);

  const selectBlocker = useCallback((id) => {
    setBlocker(id);
    setCommitError('');
  }, []);

  const goNext = useCallback(() => {
    if (step === 0) {
      if (!blocker) return;
      persistBlocker(blocker, detail);
    }
    if (step >= STEP_COUNT - 1) return;
    goTo(step + 1);
  }, [blocker, detail, goTo, step]);

  const resetHoldVisual = useCallback(() => {
    if (fillRef.current) fillRef.current.style.transform = 'scaleX(0)';
    holdButtonRef.current?.classList.remove(styles.holdHot, styles.holdPressed);
  }, []);

  const cancelHold = useCallback(() => {
    if (holdRef.current.done) return;
    cancelAnimationFrame(holdRef.current.raf);
    holdRef.current.active = false;
    resetHoldVisual();
  }, [resetHoldVisual]);

  const completeCommit = useCallback(async () => {
    persistBlocker(blocker, detail);
    capture(posthog, ONBOARDING_EVENT, {
      step: 'extended_welcome_commit',
      blocker: blocker || 'unspecified',
      entry,
    });
    setCommitError('');
    holdRef.current.timer = window.setTimeout(() => {
      Promise.resolve(onCommitted?.({ blocker: blockerLabel(blocker), detail }))
        .catch((error) => {
          holdRef.current.done = false;
          resetHoldVisual();
          const message = error instanceof Error ? error.message : 'Could not continue. Please try again.';
          setCommitError(message);
        });
    }, 280);
  }, [blocker, detail, entry, onCommitted, posthog, resetHoldVisual]);

  const startHold = useCallback(() => {
    if (holdRef.current.active || holdRef.current.done) return;
    holdRef.current.active = true;
    holdButtonRef.current?.classList.add(styles.holdPressed);
    const started = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - started) / HOLD_MS);
      if (fillRef.current) fillRef.current.style.transform = `scaleX(${progress})`;
      holdButtonRef.current?.classList.toggle(styles.holdHot, progress > 0.42);
      if (progress >= 1) {
        holdRef.current.active = false;
        holdRef.current.done = true;
        void completeCommit();
        return;
      }
      holdRef.current.raf = requestAnimationFrame(tick);
    };
    holdRef.current.raf = requestAnimationFrame(tick);
  }, [completeCommit]);

  const onHoldStart = useCallback((event) => {
    if (event.type === 'keydown') {
      if (event.repeat) return;
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      startHold();
      return;
    }
    if (event.button != null && event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    startHold();
  }, [startHold]);

  const onHoldEnd = useCallback((event) => {
    if (event.type === 'keyup') {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
    }
    cancelHold();
  }, [cancelHold]);

  const showContinue = step === 0 || step === 1 || step === 3;
  const continueDisabled = step === 0 && !blocker;

  return (
    <main
      className={styles.screen}
      data-theme={current.theme}
      data-stepper={current.stepper}
      aria-label={`Extended welcome, step ${step + 1} of ${STEP_COUNT}`}
      style={{ '--step-dir': direction }}
    >
      {STEPS.map((item, index) => (
        <div
          key={item.id}
          className={index === step ? `${styles.backdrop} ${styles.backdropActive}` : styles.backdrop}
          aria-hidden="true"
        >
          <div
            className={styles.backdropPhoto}
            style={{ backgroundImage: `url(${item.background})` }}
          />
        </div>
      ))}
      <svg className={styles.panel} viewBox="0 0 1440 900" preserveAspectRatio="none" aria-hidden="true">
        <path className={styles.panelShape} d={current.panel} fill="var(--panel)" />
      </svg>

      <div className={styles.frame}>
        <header className={styles.topBar}>
          <Brand />
          <Stepper step={step} />
        </header>

        {catalogLoading && (
          <div className={styles.stage}>
            <p className={styles.lede}>Loading…</p>
          </div>
        )}
        {!catalogLoading && !flow && (
          <div className={styles.stage}>
            <h1 className={styles.title}>We couldn’t load this step.</h1>
            <p className={styles.lede} role="alert">{catalogError || 'Please try again.'}</p>
            <button type="button" className={styles.letsGo} onClick={retryCatalog}>
              Try again
            </button>
          </div>
        )}
        {flow && step === 0 && (
          <BlockerStep
            blockers={flow.blockers}
            copy={flowCopy.blocker}
            blocker={blocker}
            detail={detail}
            onSelect={selectBlocker}
            onDetail={setDetail}
          />
        )}
        {flow && step === 1 && (
          <InsightStep
            insight={(flow.insights || {})[blocker] || (flow.insights || {}).clarity}
            copy={flowCopy.insight}
          />
        )}
        {flow && step === 2 && (
          <FutureStep
            letter={(flow.future_letters || {})[blocker] || (flow.future_letters || {}).clarity}
            copy={flowCopy.future}
            firstName={firstName}
            dates={dates}
            onContinue={goNext}
            onChangeAnswer={() => goTo(0)}
          />
        )}
        {flow && step === 3 && (
          <ExperienceStep
            pillars={flow.pillars || []}
            copy={flowCopy.experience}
            activeModule={modulePreview?.module ?? null}
            onHoverModule={showModule}
            onTouchModule={showModuleFromTouch}
            onLeaveModule={leaveModule}
            onSlideChange={closeModule}
          />
        )}
        {flow && step === 4 && (
          <CommitStep
            copy={flowCopy.commit}
            onHoldStart={onHoldStart}
            onHoldEnd={onHoldEnd}
            fillRef={fillRef}
            buttonRef={holdButtonRef}
            commitError={commitError}
          />
        )}

        <footer className={styles.footer}>
          <button type="button" className={styles.back} onClick={goBack}>
            <Icon name="arrowLeft" className={styles.btnIcon} />
            {flowCopy.back || 'Back'}
          </button>
          {flow && showContinue && (
            <button
              type="button"
              className={styles.continue}
              onClick={goNext}
              disabled={continueDisabled}
            >
              {step === 1 ? flowCopy.insight?.continue_label : flowCopy.continue}
              <Icon name="arrowRight" className={styles.btnIcon} />
            </button>
          )}
        </footer>
      </div>
      {modulePreview ? (
        <ModuleDialog
          module={modulePreview.module}
          closeLabel={flowCopy.experience?.close_label}
          dismissOnBackdrop={modulePreview.touch}
          onClose={closeModule}
          onDialogEnter={cancelModuleHide}
          onDialogLeave={leaveDialog}
        />
      ) : null}
    </main>
  );
};

export default ExtendedWelcomeFlow;
